import { memo, useCallback, useEffect, useState } from "react";
import OffCanvas from "../../../components/offCanvas";
import {
  ColDef,
  ICellRendererParams,
  ValueGetterParams,
} from "ag-grid-community";
import { formatDateTimeToLocal } from "../dateFormat";
import { ToasterObject } from "../interface-utils";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as MenuIcon } from "../../../assets/img/grid-menu.svg";
import { Modal, Toast } from "react-bootstrap";
import AddAppointmentRadio from "../AddAppointmentRadio";
import { createAppointmentApi } from "../../../api-helpers/AppointmentApiHelper";
import { GenericGrid } from "../../../grid/GenericGrid";
import {
  AppointmentCancellationReason,
  AppointmentHistory,
  AppointmentMode,
  AppointmentRescheduledReason,
  AppointmentType,
  GridDataRequest,
} from "appointment-experience-client";
import { appointmentDetailsPath } from "../../../constants/Appointment";
import { PatientPersonalInfo } from "patients-experience-client";
import { GridDataResponse } from "appointment-experience-client";
import { FilterCriteria } from "appointment-experience-client";
import ScheduleHeader from "./ScheduleHeader";
import StatusBadgeRenderer from "../../../renderers/StatusBadge";
import { UserContextHolder } from "../../../auth/UserContextHolder";
import { createGridApi } from "../../../api-helpers/GridApiHelper";
import { UserNameResolver } from "../../../grid/resolvers/UserNameResolver";
import { workflowStatesCache } from "../../../grid/renderers/WorkflowStateCellValueGetter";
import {
  AppointmentHistoryConstants,
  WorkflowConstants,
} from "../../../helper/WorkflowConstants";
import { UserName } from "../../../components/UserName";
import Spinner from "../../../components/Spinner";
import { RescheduledCountRenderer } from "../../../renderers/RescheduledCountRenderer";
import { createWorkflowApi } from "../../../api-helpers/CommonUtilsApiHelper";
import { WorkflowState } from "common-util-experience-client";
import { Workflow } from "../../../constants/Lookups";
import { isScheduling2Enabled } from "../../../constants/FeatureFlags";
import { NEW_APPOINTMENT_PARAM } from "../scheduleLinks";

interface Props {
  patientId?: string;
  breadcrumbs?: boolean | undefined;
  scope: "Patient" | "Global";
  patient?: PatientPersonalInfo;
}

export const ScheduleGrid = ({ patientId, breadcrumbs, scope, patient }: Props) => {
  const navigate = useNavigate();
  const appointmentApi = createAppointmentApi();
  const workflowApi = createWorkflowApi();
  const gridApi = createGridApi();
  const [openToaster, setOpenToaster] = useState<ToasterObject>({
    message: "",
    show: false,
    type: "",
  });

  const [initialFiltersReady, setInitialFiltersReady] = useState(false);
  const [gridReloadKey, setGridReloadKey] = useState(0);

  const [offCanvasComp, setOffCanvasComp] = useState("");
  const [offCanvasShow, setOffCanvasShow] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // ?new=1 asks us to open the create drawer on arrival (see scheduleLinks.ts).
  const openNewAppointment =
    searchParams.get(NEW_APPOINTMENT_PARAM) === "1";
  const [loading, setLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<FilterCriteria[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [appointmentHistory, setAppointmentHistory] = useState<
    AppointmentHistory[] | null
  >(null);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>(
    [],
  );
  const [appointmentModes, setAppointmentModes] = useState<AppointmentMode[]>(
    [],
  );
  const [cancelledStateId, setCancelledStateId] = useState<string>("");
  const [rescheduleStateId, setRescheduleStateId] = useState<string>("");
  const [rescheduledReasons, setRescheduledReasons] = useState<
    AppointmentRescheduledReason[]
  >([]);
  const [cancelledReasons, setCancelledReasons] = useState<
    AppointmentCancellationReason[]
  >([]);

  useEffect(() => {
    UserContextHolder.addListener(() => {
      setCurrentUserId(UserContextHolder.getUser()?.id);
    });
    getAllAppointmentModes();
    getAllAppointmentTypes();
    getStatusId();
    getReasons();
  }, []);

  useEffect(() => {
    if (initialFiltersReady) {
      return;
    }

    if (!currentUserId) {
      return;
    }

    setInitialFiltersReady(true);
  }, [filters]);

  useEffect(() => {
    if (!openToaster.show) {
      return;
    }
    const interval = setTimeout(() => {
      setOpenToaster({ ...openToaster, show: false });
    }, 3000);
    return () => clearTimeout(interval);
  }, [openToaster]);

  const reloadGrid = () => {
    setGridReloadKey(gridReloadKey + 1);
  };

  const handleOffCanvasShow = (comp: any) => {
    setOffCanvasComp(comp);
    setOffCanvasShow(!offCanvasShow);
  };

  // Arriving with ?new=1 opens the create drawer, then drops the flag so closing it
  // or stepping back through history does not reopen it. The offcanvas state is set
  // directly rather than through handleOffCanvasShow, which toggles.
  useEffect(() => {
    if (!openNewAppointment) {
      return;
    }
    setOffCanvasComp("newAppointmentV2");
    setOffCanvasShow(true);
    navigate(location.pathname, { replace: true });
  }, [openNewAppointment]);

  // @TODO: add  appointment id  to the VIEW and Request model
  const handleClick = (props: any) => {
    navigate(
      appointmentDetailsPath(props.data.appointmentId, props.data.patientId)
    );
  };
  const ViewIcon = (props: any) => {
    return (
      <div role="button" onClick={() => handleClick(props)}>
        <MenuIcon />
      </div>
    );
  };

  const getAllAppointmentTypes = async () => {
    const appointmentTypes =
      await appointmentApi.appointmentControllerGetAllAppointmentTypes();
    setAppointmentTypes(appointmentTypes.data);
  };

  const getAllAppointmentModes = async () => {
    const appointmentModes =
      await appointmentApi.appointmentControllerGetAppointmentMode();
    setAppointmentModes(appointmentModes.data);
  };

  const getAppointmentHistory = async (appointmentId: string) => {
    const history =
      await appointmentApi.appointmentControllerGetAppointmentHistories(
        appointmentId,
      );
    const sortedHistory = history.data.sort((a, b) => {
      const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return dateB - dateA;
    });
    setAppointmentHistory(sortedHistory);
  };

  const getAppointmentTypeName = (typeId: string) => {
    return appointmentTypes.filter((type) => type.id === typeId)[0].name;
  };

  const getAppointmentModeName = (modeId: string) => {
    return appointmentModes.filter((mode) => mode.id === modeId)[0].name;
  };

  const openInNewTab = (url: string) => {
    const newWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  };

  const colDefs: ColDef[] = [
    {
      field: "appointmentType",
      minWidth: 280,
      headerName: "Appointment Type",
      pinned: "left",
      sortable: true,
    },
    {
      field: "patientName",
      minWidth: 200,
      headerName: "Patient(s)",
      pinned: "left",
      sortable: true,
      cellRenderer: (params: any) => {
        return (
          <a
            href="#"
            className="a-link a-underline text-secondary"
            onClick={() => {
              openInNewTab(`/patients/${params.data["patientId"]}/summary`);
            }}
          >
            {params.data["patientName"]}
          </a>
        );
      },
    },
    {
      field: "appointmentMode",
      minWidth: 180,
      headerName: "Mode",
      suppressMenu: true,
      sortable: true,
    },
    {
      field: "coachName",
      minWidth: 180,
      headerName: "Coach",
      sortable: true,
    },
    {
      field: "time",
      minWidth: 240,
      headerName: "Date/Time",
      valueFormatter: function (params: any) {
        return formatDateTimeToLocal(params.data.time);
      },
      sort: scope === "Global" ? "asc" : "desc",
      sortable: true,
    },
    {
      field: "durationMinutes",
      minWidth: 80,
      headerName: "Duration",
      valueFormatter: function (params: any) {
        return params.data.durationMinutes + " min";
      },
      sortable: true,
    },
    {
      field: "wfStateName",
      minWidth: 90,
      headerName: "Status",
      cellRenderer: StatusBadgeRenderer,
      sortable: true,
    },
    {
      field: "appointmentId",
      headerName: "",
      width: 80,
      minWidth: 80,
      cellRenderer: (params: ValueGetterParams) => {
        return (
          <span
            onClick={async () => {
              await setLoading(true);
              await getAllAppointmentModes();
              await getAllAppointmentTypes();
              await getAppointmentHistory(params.data.appointmentId);
              setShowModal(true);
              await setLoading(false);
            }}
          >
            <span
              className="material-symbols-outlined fs-20 text-secondary"
              style={{ cursor: "pointer" }}
            >
              history
            </span>
          </span>
        );
      },
    },
    {
      field: "appointmentId",
      minWidth: 200,
      headerName: "Rescheduled #",
      cellRendererSelector: (params: ICellRendererParams) => {
        return {
          component: RescheduledCountRenderer,
          params: { data: params.data },
        };
      },
    },
    { headerName: "", width: 60, cellRenderer: ViewIcon, pinned: "right" },
  ];

  const loadData = async (
    request: GridDataRequest,
  ): Promise<GridDataResponse> => {
    const result = await gridApi.gridControllerGetAppointmentGridData(request);
    return result.data;
  };

  const getStatusId = async (): Promise<void> => {
    const workflowDetails =
      await workflowApi.workflowControllerGetStatesForWorkflow(
        WorkflowConstants.appointmentWorkflowType,
      );
    const appointment: WorkflowState[] = workflowDetails.data;
    const cancelled = appointment.find(
      (state: WorkflowState) =>
        state.stateName === Workflow.APPOINTMENT_STATUS_CANCELED,
    );
    const reschedule = appointment.find(
      (state: WorkflowState) =>
        state.stateName === Workflow.APPOINTMENT_STATUS_RESCHEDULED,
    );
    setCancelledStateId(cancelled!.stateId);
    setRescheduleStateId(reschedule!.stateId);
  };

  const getReasons = async () => {
    const rescheduleReasons =
      await appointmentApi.appointmentControllerGetAppointmentRescheduledReasons();
    const cancelledReasons =
      await appointmentApi.appointmentControllerGetAppointmentCancellationReasons();

    setRescheduledReasons(rescheduleReasons.data);
    setCancelledReasons(cancelledReasons.data);
  };
  return (
    <>
      <Toast
        className={`toast toast-${
          openToaster.type === "success"
            ? "success"
            : openToaster.type === "error"
              ? "danger"
              : ""
        } top-right mt-10 move-in`}
        show={openToaster.show}
        onClose={() => setOpenToaster({ message: "", show: false, type: "" })}
      >
        <div className="ps-3">
          <span className="material-symbols-outlined fs-36">
            {openToaster.type === "success"
              ? "check_circle_outline"
              : openToaster.type === "error"
                ? "error_outline"
                : ""}
          </span>
        </div>
        <Toast.Body className="flex-fill">
          <div className="fs-14">
            {openToaster.type === "success"
              ? `${openToaster.message} added successfully!`
              : openToaster.type === "error"
                ? `${openToaster.message}`
                : ""}
          </div>
        </Toast.Body>
        <Toast.Header></Toast.Header>
      </Toast>

      <div>
        {/* @TODO: ScheduleHeader */}
        {currentUserId && (
          <main className="main">
            <ScheduleHeader
              scope={scope}
              breadcrumbs={breadcrumbs}
              patientId={patientId}
              currentUserId={currentUserId}
              offCanvasShowCallback={handleOffCanvasShow}
              onUpdateFilters={setFilters}
            />
            <div className="d-flex px-3 px-md-4 px-lg-5 d-flex align-items-stretch">
              <div className="flex-fill">
                <div className="py-4">
                  {initialFiltersReady && (
                    <GenericGrid
                      gridId="appointment-grid"
                      colDefs={colDefs}
                      dataProvider={loadData}
                      filters={filters}
                      key={gridReloadKey}
                    ></GenericGrid>
                  )}
                </div>
              </div>
            </div>
            <OffCanvas
              title={"Add Appointment"}
              offCanvasShow={offCanvasShow}
              setOffCanvasShow={setOffCanvasShow}
              offCanvasComp={offCanvasComp}
              placement={offCanvasComp === "newAppointmentV2" ? "top" : "end"}
              className={offCanvasComp === "eventCalendar" ? "w-95" : ""}
              backdrop={offCanvasComp === "newAppointmentV2" ? "static" : true}
            >
              <AddAppointmentRadio
                setOpenToaster={setOpenToaster}
                offCanvasShow={offCanvasShow}
                offCanvasComp={offCanvasComp}
                setOffCanvasShow={setOffCanvasShow}
                setOffCanvasComp={setOffCanvasComp}
                reloadGrid={reloadGrid}
                patient={patient}
              />
            </OffCanvas>
          </main>
        )}
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          aria-labelledby="contained-modal-title-vcenter"
          centered
        >
          <>
            <Modal.Header closeButton>
              <Modal.Title id="contained-modal-title-vcenter">
                History
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {appointmentHistory?.map(
                (history: AppointmentHistory, historyIndex: number) => {
                  const isTimeField =
                    history.fieldChanged ===
                    AppointmentHistoryConstants.APPOINTMENT_FIELD_TIME;
                  const isStatusField =
                    history.fieldChanged ===
                    AppointmentHistoryConstants.APPOINTMENT_STATUS_FIELD;
                  const isCancelledStatus =
                    history.newValue === cancelledStateId;

                  let reasonLabel: string | null = null;

                  if (isTimeField && history.reason) {
                    const found = rescheduledReasons.find(
                      (r) => r.id === history.reason,
                    );
                    reasonLabel = found?.reasonName || null;
                  }

                  if (isStatusField && isCancelledStatus && history.reason) {
                    const found = cancelledReasons.find(
                      (r) => r.id === history.reason,
                    );
                    reasonLabel = found?.reasonName || null;
                  }

                  return (
                    <div key={historyIndex}>
                      <div className="border rounded mt-4">
                        <div className="d-flex align-items-start px-3 py-2 fs-14">
                          <div className="flex-fill">
                            {history.fieldChanged ===
                            AppointmentHistoryConstants.APPOINTMENT_FIELD_CREATED ? (
                              <>
                                Created By:{" "}
                                <UserName userId={history.newValue} />
                              </>
                            ) : (
                              <>
                                {isTimeField
                                  ? `Rescheduled: `
                                  : `Updated ${history.fieldChanged}: `}
                                {isStatusField
                                  ? workflowStatesCache.get(history.oldValue)
                                  : isTimeField
                                    ? formatDateTimeToLocal(history.oldValue)
                                    : history.fieldChanged ===
                                        AppointmentHistoryConstants.APPOINTMENT_FIELD_MODE
                                      ? getAppointmentModeName(history.oldValue)
                                      : history.fieldChanged ===
                                          AppointmentHistoryConstants.APPOINTMENT_FIELD_TYPE
                                        ? getAppointmentTypeName(
                                            history.oldValue,
                                          )
                                        : history.oldValue}
                                {" -> "}
                                {isStatusField
                                  ? workflowStatesCache.get(history.newValue)
                                  : isTimeField
                                    ? formatDateTimeToLocal(history.newValue)
                                    : history.fieldChanged ===
                                        AppointmentHistoryConstants.APPOINTMENT_FIELD_MODE
                                      ? getAppointmentModeName(history.newValue)
                                      : history.fieldChanged ===
                                          AppointmentHistoryConstants.APPOINTMENT_FIELD_TYPE
                                        ? getAppointmentTypeName(
                                            history.newValue,
                                          )
                                        : history.newValue}

                                {reasonLabel && (
                                  <div className="text-muted mt-1">
                                    <strong>
                                      {isCancelledStatus
                                        ? "Cancellation Reason:"
                                        : "Reschedule Reason:"}
                                    </strong>{" "}
                                    {reasonLabel}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="d-flex bg-gray-FAF justify-content-between fs-12 px-3 py-2">
                          <div className="text-light">
                            {formatDateTimeToLocal(
                              history.createdAt.toString(),
                            )}
                          </div>
                          <div className="d-flex align-items-center text-base">
                            <span className="material-symbols-outlined fs-18 me-1">
                              person
                            </span>
                            <span>
                              <UserName userId={history.createdBy} />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                },
              )}

              <div className="text-center">
                {!loading && !appointmentHistory?.length
                  ? "No History to show"
                  : ""}
              </div>
            </Modal.Body>
          </>
        </Modal>
      )}
    </>
  );
};
